import { MAX_LINES } from '../../pages/checkout/model/GiftOptions';
import {
    EMPTY_ORDER_ADDRESS,
    customerAddressToOrderAddress,
    isValidAddress,
    isValidContactInfo,
} from '../../utils/address-utils';
import { isShippable } from '../../utils/product-utils';
import { PaymentMethodId } from '../baskets/model';
import { BasketsType } from '../baskets/model/BasketType';
import { ShippingMethodId } from '../baskets/model/ShippingMethodId';
import { DEFAULT_SHIPMENT } from './shipment-utils';
import type { Basket, CustomerInfo, OrderAddress, Shipment } from '../baskets/model';
import type { BasketGiftCardData } from '../baskets/model/BasketGiftCardData';
import type { CustomerAddress } from '../customers/model';

const DEFAULT_SHIPMENT_ID = 'me';

export const getShippingDiscount = (basket?: Basket): number => {
    if (!basket) {
        return 0;
    }

    const { shippingTotal: basketShippingTotal = 0, shipments = [] } = basket;

    const shipmentTotal = shipments.reduce((acc, { shippingTotal = 0 }) => acc + shippingTotal, 0);

    return Math.abs(Math.round((basketShippingTotal - shipmentTotal) * 100) / 100);
};

export const getShipToShippingTotal = (basket?: Basket): number => {
    if (!basket) {
        return 0;
    }

    const { shippingItems = [] } = basket;

    return shippingItems[0]?.priceAfterItemDiscount ?? shippingItems[0]?.price ?? 0;
};

export const getSDDShippingTotal = (basket?: Basket): number => {
    if (!basket) {
        return 0;
    }

    const { shipments = [] } = basket;

    return shipments
        .filter((shipment) => shipment.shippingMethod?.id === ShippingMethodId.SAME_DAY_DELIVERY)
        .reduce((acc, shipment) => acc + (shipment.shippingTotal ?? 0), 0);
};

export const getCouponPromotionAmount = (basket?: Basket): number => {
    if (!basket) {
        return 0;
    }

    const { couponItems = [] } = basket;

    const promotionAmount = couponItems.find(({ c_promotionAmount }) => !!c_promotionAmount)
        ?.c_promotionAmount as number;

    return promotionAmount ?? 0;
};

export const getGiftCardData = (basket?: Basket): BasketGiftCardData => {
    if (!basket) {
        return {
            quantityApplied: 0,
            remainingPayment: 0,
            totalValueApplied: 0,
            hasGiftCardsApplied: false,
            hasRemainingPayment: false,
            isFullyPaidWithGiftCards: false,
        };
    }

    const { orderTotal = 0, paymentInstruments = [] } = basket;

    const giftCards = paymentInstruments.filter(
        ({ paymentMethodId }) => paymentMethodId === PaymentMethodId.GIFT_CARD,
    );

    const giftCardTotalApplied = giftCards.reduce(
        (acc, item) => acc + ((item.amount as number) ?? 0),
        0,
    );

    const remainingPayment = orderTotal - giftCardTotalApplied;
    const isFullyPaidWithGiftCards = remainingPayment <= 0 && orderTotal > 0;

    return {
        quantityApplied: giftCards.length,
        totalValueApplied: giftCardTotalApplied,
        hasGiftCardsApplied: giftCards.length > 0,
        hasRemainingPayment: remainingPayment > 0,
        remainingPayment: remainingPayment >= 0 ? remainingPayment : 0,
        isFullyPaidWithGiftCards,
    };
};

export const getShipment = (basket: Basket | undefined): Shipment => {
    if (!basket) {
        return DEFAULT_SHIPMENT;
    }

    const { shipments = [] } = basket;

    let shipment: Shipment | undefined;

    if (isBopisBasket(basket)) {
        shipment = getShipmentByMethodId(basket, ShippingMethodId.BOPIS);
    } else {
        shipment = getShipmentByMethodId(basket, ShippingMethodId.SAME_DAY_DELIVERY);
    }

    if (!shipment) {
        [shipment] = shipments;
    }

    return shipment ?? DEFAULT_SHIPMENT;
};

export const getShipmentByMethodId = (
    basket: Basket | undefined,
    methodId: ShippingMethodId,
): Shipment | undefined =>
    basket?.shipments?.find(({ shippingMethod }) => shippingMethod?.id === methodId);

export const getDefaultShipment = ({ shipments = [] }: Basket): Shipment | undefined =>
    shipments.find(({ shipmentId }) => shipmentId === DEFAULT_SHIPMENT_ID);

export const getShippingAddress = (
    basket: Basket | undefined,
    customerDefaultAddress?: CustomerAddress,
): OrderAddress | undefined => {
    if (!basket) {
        return customerAddressToOrderAddress(customerDefaultAddress);
    }

    const hasGiftRegistryItems = hasGiftRegistryProducts(basket);
    const defaultShipment = getDefaultShipment(basket);

    let shippingAddress: OrderAddress | undefined = defaultShipment?.shippingAddress;

    if (hasGiftRegistryItems && defaultShipment?.shippingAddress?.c_isGiftRegistryAddress) {
        shippingAddress = defaultShipment?.shippingAddress;
    } else if (isBopisBasket(basket)) {
        shippingAddress = getShipmentByMethodId(basket, ShippingMethodId.BOPIS)?.shippingAddress;
    } else {
        shippingAddress = getShipmentByMethodId(
            basket,
            ShippingMethodId.SAME_DAY_DELIVERY,
        )?.shippingAddress;
    }

    if (
        !shippingAddress &&
        defaultShipment?.shippingAddress &&
        (!defaultShipment?.shippingAddress.c_isGiftRegistryAddress || hasGiftRegistryItems)
    ) {
        shippingAddress = defaultShipment?.shippingAddress;
    }

    const validAddress = hasShippingProducts(basket)
        ? isValidAddress(shippingAddress)
        : isValidContactInfo(shippingAddress);
    return !customerDefaultAddress || validAddress
        ? shippingAddress
        : customerAddressToOrderAddress(customerDefaultAddress);
};

export const getShippingAddressOrEmpty = (
    basket: Basket | undefined,
    customerDefaultAddress?: CustomerAddress,
): OrderAddress => getShippingAddress(basket, customerDefaultAddress) ?? EMPTY_ORDER_ADDRESS;

export const getShippingAddressByMethodId = (
    basket: Basket | undefined,
    methodId: ShippingMethodId,
): OrderAddress | undefined =>
    basket?.shipments?.find(({ shippingMethod }) => shippingMethod?.id === methodId)
        ?.shippingAddress;

export const isBopisBasket = (basket: Basket | undefined): boolean =>
    basket?.c_cartType === BasketsType.BOPIS;

export const hasGiftRegistryProducts = (basket: Basket | undefined): boolean =>
    !!basket?.productItems?.some(({ c_GiftRegistryLineItem }) => !!c_GiftRegistryLineItem);

export const hasShippingProducts = (basket: Basket | undefined): boolean =>
    !!basket?.productItems?.some((item) => isShippable(item));

export const isSameCustomerInfo = (
    customerInfo: CustomerInfo | undefined,
    email: string | undefined,
    fullName: string | undefined,
): boolean => !!(email === customerInfo?.email && fullName === customerInfo?.customerName);

type GiftCardMessageFields = {
    c_gcPersonalMessage1?: string | null;
    c_gcPersonalMessage2?: string | null;
    c_gcPersonalMessage3?: string | null;
    c_gcPersonalMessage4?: string | null;
    giftMessage?: string | null;
};

export const getFourGiftCardMessagesAsOneString = ({
    c_gcPersonalMessage1,
    c_gcPersonalMessage2,
    c_gcPersonalMessage3,
    c_gcPersonalMessage4,
    giftMessage,
}: GiftCardMessageFields): string | undefined => {
    if (giftMessage) {
        return giftMessage.trim();
    }

    return [
        c_gcPersonalMessage1?.trim(),
        c_gcPersonalMessage2?.trim(),
        c_gcPersonalMessage3?.trim(),
        c_gcPersonalMessage4?.trim(),
    ]
        .filter(Boolean)
        .join(' ')
        .trim();
};

export const getFourGiftCardMessages = (
    giftCardMessage?: string | null,
): GiftCardMessageFields | undefined => {
    if (!giftCardMessage) {
        return undefined;
    }

    const lines = giftCardMessage
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .slice(0, MAX_LINES);

    return {
        giftMessage: giftCardMessage.trim(),
        c_gcPersonalMessage1: lines[0] || '',
        c_gcPersonalMessage2: lines[1] || '',
        c_gcPersonalMessage3: lines[2] || '',
        c_gcPersonalMessage4: lines[3] || '',
    };
};

export const isBasketEmpty = (basket: Basket | undefined): boolean =>
    !basket?.basketId || (basket.productItems ?? []).length === 0;
